import { Avatar, Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { getColor } from 'components/@home/drawers/AcknowledgeCreateDrawer/Form/signerColors';
import { array, bool, func, number, object } from 'prop-types';
import React from 'react';
import initialsFromUsername from 'utils/initialsFromUsername';
import { I18n } from 'utils/i18n';

/** Signing mode + current signer (shared PDF only), aligned like AcknowledgeCreateDrawer `FieldToolbar`. */
const reassignSignaturesToFirstSigner = fieldsList =>
  (fieldsList || []).map(f => (f?.type === 'signature' ? { ...f, signerIndex: 0 } : f));

const SetupDocumentSigningToolbar = ({
  template,
  setTemplate,
  fields,
  placeholderMembers,
  currentSignerIndex,
  setCurrentSignerIndex,
  syncFieldsToTemplate,
  stacked = false,
  disabled = false,
}) => (
  <Box
    sx={{
      display: 'flex',
      flexWrap: stacked ? 'nowrap' : 'wrap',
      flexDirection: stacked ? 'column' : 'row',
      alignItems: stacked ? 'stretch' : 'flex-end',
      gap: 2,
      width: stacked ? '100%' : undefined,
      mt: 1,
    }}
  >
    <FormControl
      size="small"
      sx={{ minWidth: stacked ? 0 : undefined, width: stacked ? '100%' : undefined }}
    >
      <InputLabel id="tpl-signing-mode-label">
        {I18n.t('AcknowledgementCreate.SigningMode.label')}
      </InputLabel>
      <Select
        labelId="tpl-signing-mode-label"
        value={template.signingMode || 'onePerMember'}
        label={I18n.t('AcknowledgementCreate.SigningMode.label')}
        disabled={disabled}
        sx={{
          minWidth: stacked ? 0 : { xs: '100%', sm: 280 },
          width: stacked ? '100%' : undefined,
        }}
        onChange={e => {
          const mode = e.target.value;
          setTemplate(t => ({ ...t, signingMode: mode }));
          syncFieldsToTemplate(reassignSignaturesToFirstSigner(fields));
          setCurrentSignerIndex(0);
        }}
      >
        <MenuItem value="onePerMember">
          {I18n.t('HrBot.EditTemplate.SigningMode.onePerMember')}
        </MenuItem>
        <MenuItem value="oneForAll" disabled={placeholderMembers.length > 50}>
          {I18n.t('HrBot.EditTemplate.SigningMode.oneForAll')}
          {placeholderMembers.length > 50 &&
            ` (${I18n.t('AcknowledgementCreate.SigningMode.tooManyMembers')})`}
        </MenuItem>
      </Select>
    </FormControl>
    {template.signingMode === 'oneForAll' && placeholderMembers.length > 0 && (
      <FormControl
        size="small"
        sx={{ minWidth: stacked ? 0 : undefined, width: stacked ? '100%' : undefined }}
      >
        <InputLabel id="tpl-signer-label">
          {I18n.t('AcknowledgementCreate.SignerSelector.label')}
        </InputLabel>
        <Select
          labelId="tpl-signer-label"
          value={currentSignerIndex}
          label={I18n.t('AcknowledgementCreate.SignerSelector.label')}
          disabled={disabled}
          sx={{
            minWidth: stacked ? 0 : { xs: '100%', sm: 220 },
            width: stacked ? '100%' : undefined,
          }}
          onChange={e => setCurrentSignerIndex(Number(e.target.value))}
        >
          {placeholderMembers.map((member, index) => {
            const color = getColor(index);
            const label = `${member.firstName || ''} ${member.surName || ''}`.trim();
            return (
              <MenuItem key={member.user?._id || index} value={index}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: color.text,
                      flexShrink: 0,
                    }}
                  />
                  <Avatar sx={{ width: 24, height: 24 }} src={member.avatar}>
                    {initialsFromUsername(label)}
                  </Avatar>
                  <span>{label}</span>
                </Box>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    )}
  </Box>
);

SetupDocumentSigningToolbar.propTypes = {
  template: object.isRequired,
  setTemplate: func.isRequired,
  fields: array.isRequired,
  placeholderMembers: array.isRequired,
  currentSignerIndex: number.isRequired,
  setCurrentSignerIndex: func.isRequired,
  syncFieldsToTemplate: func.isRequired,
  stacked: bool,
  disabled: bool,
};

export default SetupDocumentSigningToolbar;
